import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Form, Badge } from "react-bootstrap";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase";
import { sortAvailableLotsNewestFirst } from "../../../../utils/accessoryLotInventory";

const WithdrawModal = ({ show, onHide, selectedItems, accessories, onSubmit }) => {
  const [withdrawUpdates, setWithdrawUpdates] = useState({});
  const [withdrawNotes, setWithdrawNotes] = useState({});
  const [lotsByItem, setLotsByItem] = useState({});
  const [selectedLotIds, setSelectedLotIds] = useState({});
  const [loadingLots, setLoadingLots] = useState(false);
  const [lotLoadError, setLotLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedAccessories = accessories.filter((item) =>
    selectedItems.includes(item.id)
  );

  useEffect(() => {
    if (!show) return undefined;

    const selected = accessories.filter((item) =>
      selectedItems.includes(item.id)
    );
    const trackedItems = selected.filter(
      (item) => item.lotTrackingVersion === 1
    );
    const initialUpdates = {};
    const initialNotes = {};
    selected.forEach((item) => {
      initialUpdates[item.id] = "";
      initialNotes[item.id] = "";
    });
    setWithdrawUpdates(initialUpdates);
    setWithdrawNotes(initialNotes);
    setLotsByItem({});
    setSelectedLotIds({});
    setLotLoadError("");
    setSubmitting(false);

    if (trackedItems.length === 0) {
      setLoadingLots(false);
      return undefined;
    }

    let active = true;
    setLoadingLots(true);
    Promise.all(
      trackedItems.map(async (item) => {
        const snapshot = await getDocs(
          collection(db, "accessories", item.id, "lots")
        );
        const lots = sortAvailableLotsNewestFirst(
          snapshot.docs.map((lotDoc) => ({
            id: lotDoc.id,
            ...lotDoc.data(),
          }))
        );
        return [item.id, lots];
      })
    )
      .then((entries) => {
        if (!active) return;
        const loadedLots = Object.fromEntries(entries);
        setLotsByItem(loadedLots);
        setSelectedLotIds(
          Object.fromEntries(
            entries.map(([itemId, lots]) => [
              itemId,
              lots.length > 0 ? lots[0].id : "",
            ])
          )
        );
      })
      .catch((error) => {
        console.error("Error loading accessory lots for adjustment:", error);
        if (active) {
          setLotLoadError(
            error.message || "Failed to load lots for stock adjustment."
          );
        }
      })
      .finally(() => {
        if (active) setLoadingLots(false);
      });

    return () => {
      active = false;
    };
  }, [show, selectedItems, accessories]);

  const handleQuantityChange = (itemId, value) => {
    setWithdrawUpdates((previous) => ({
      ...previous,
      [itemId]: value,
    }));
  };

  const handleNotesChange = (itemId, value) => {
    setWithdrawNotes((previous) => ({
      ...previous,
      [itemId]: value,
    }));
  };

  const handleLotChange = (itemId, lotId) => {
    setSelectedLotIds((previous) => ({
      ...previous,
      [itemId]: lotId,
    }));
    setWithdrawUpdates((previous) => ({
      ...previous,
      [itemId]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const updates = [];

    if (selectedAccessories.length !== selectedItems.length) {
      alert("One or more selected accessories no longer exist. Reload and try again.");
      return;
    }

    for (const item of selectedAccessories) {
      const currentStock = Number(item.stockQuantity) || 0;
      const amountToWithdraw = Number(withdrawUpdates[item.id]);
      const tracked = item.lotTrackingVersion === 1;
      const lotId = tracked ? selectedLotIds[item.id] : null;
      const selectedLot = tracked
        ? (lotsByItem[item.id] || []).find((lot) => lot.id === lotId)
        : null;
      const available = tracked
        ? Number(selectedLot && selectedLot.stockQuantity) || 0
        : currentStock;

      if (!Number.isInteger(amountToWithdraw) || amountToWithdraw <= 0) {
        alert(`Enter a positive whole-number adjustment for ${item.itemName}.`);
        return;
      }
      if (tracked && !selectedLot) {
        alert(`Select an available lot for ${item.itemName}.`);
        return;
      }
      if (amountToWithdraw > available) {
        const source = tracked ? `selected lot ${selectedLot.lotNo}` : "legacy stock";
        alert(`${item.itemName} has only ${available} available in ${source}.`);
        return;
      }
      if (!(withdrawNotes[item.id] || "").trim()) {
        alert(`Please provide adjustment notes for ${item.itemName}.`);
        return;
      }

      updates.push({
        id: item.id,
        lotId,
        stockQuantity: currentStock - amountToWithdraw,
        amountWithdrawn: amountToWithdraw,
        notes: withdrawNotes[item.id],
      });
    }

    setSubmitting(true);
    try {
      await onSubmit(updates);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      size="xl"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>Stock Adjustment - {selectedItems.length} Items</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-warning">
          <strong>Note:</strong> Tracked accessories are reduced only from the
          selected lot; the newest available lot is selected by default. Legacy
          accessories are reduced from their total stock. Detailed notes are required.
        </div>

        {lotLoadError && (
          <div className="alert alert-danger">{lotLoadError}</div>
        )}

        <Form onSubmit={handleSubmit}>
          <Table striped bordered size="sm" responsive>
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Item Name</th>
                <th>Category</th>
                <th>Size</th>
                <th>Current Stock</th>
                <th style={{ minWidth: "260px" }}>Lot</th>
                <th>Adjustment Quantity</th>
                <th>New Total</th>
                <th style={{ minWidth: "200px" }}>Adjustment Notes</th>
              </tr>
            </thead>
            <tbody>
              {selectedAccessories.map((item) => {
                const currentStock = Number(item.stockQuantity) || 0;
                const tracked = item.lotTrackingVersion === 1;
                const lots = lotsByItem[item.id] || [];
                const selectedLotId = selectedLotIds[item.id] || "";
                const selectedLot = lots.find(
                  (lot) => lot.id === selectedLotId
                );
                const maxAdjustment = tracked
                  ? Number(selectedLot && selectedLot.stockQuantity) || 0
                  : currentStock;
                const amount = Number(withdrawUpdates[item.id]) || 0;

                return (
                  <tr key={item.id}>
                    <td>{item.itemCode}</td>
                    <td>{item.itemName}</td>
                    <td>{item.category}</td>
                    <td>{item.size || "N/A"}</td>
                    <td>
                      <span
                        className={
                          currentStock <= 0
                            ? "text-danger"
                            : currentStock <= (item.minimumStock || 0)
                            ? "text-warning"
                            : "text-success"
                        }
                      >
                        {currentStock}
                      </span>
                    </td>
                    <td>
                      {tracked ? (
                        <Form.Control
                          as="select"
                          size="sm"
                          required
                          disabled={loadingLots || submitting}
                          value={selectedLotId}
                          onChange={(event) =>
                            handleLotChange(item.id, event.target.value)
                          }
                        >
                          <option value="">
                            {loadingLots ? "Loading lots..." : "Select lot..."}
                          </option>
                          {lots.map((lot, index) => (
                            <option key={lot.id} value={lot.id}>
                              {lot.lotNo} — {lot.stockQuantity} available
                              {index === 0 ? " (Newest)" : ""}
                            </option>
                          ))}
                        </Form.Control>
                      ) : (
                        <Badge variant="secondary">Legacy stock (no lot)</Badge>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="1"
                        max={maxAdjustment}
                        step="1"
                        required
                        disabled={loadingLots || submitting || maxAdjustment <= 0}
                        value={withdrawUpdates[item.id] || ""}
                        onChange={(event) =>
                          handleQuantityChange(item.id, event.target.value)
                        }
                        placeholder="Amount"
                      />
                    </td>
                    <td>
                      <span
                        className={
                          currentStock - amount < 0
                            ? "text-danger font-weight-bold"
                            : "font-weight-bold"
                        }
                      >
                        {currentStock - amount}
                      </span>
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows="2"
                        size="sm"
                        required
                        disabled={submitting}
                        value={withdrawNotes[item.id] || ""}
                        onChange={(event) =>
                          handleNotesChange(item.id, event.target.value)
                        }
                        placeholder="Reason for adjustment..."
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="secondary"
              className="mr-2"
              onClick={onHide}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              type="submit"
              disabled={loadingLots || Boolean(lotLoadError) || submitting}
            >
              {submitting
                ? "Applying..."
                : `Apply Stock Adjustment (${selectedItems.length} items)`}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default WithdrawModal;
